/**
 * useSegmentVideoPlayer - Video playback with seamless transitions and preloading ALL segments
 * 
 * Features:
 * - Preloads all segments upfront for smooth playback
 * - Master clock approach (wall-clock drives playhead)
 * - Per-segment speed via video.playbackRate
 */
import { useRef, useCallback, useEffect, useState } from 'react';
import { getEffectiveDuration } from '../utils/segmentUtils';

export function useSegmentVideoPlayer({
  videoRef,
  segments = [],
  getAssetUrl,
  isPlaying,
  onPositionChange,
  onSeekComplete,
  onSegmentChange
}) {
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allPreloaded, setAllPreloaded] = useState(false);
  
  // Preloaded video elements for all segments
  const preloadedVideosRef = useRef([]);
  const rafIdRef = useRef(null);
  const lastPositionRef = useRef(0);
  const playStartTimeRef = useRef(null);
  const playStartPositionRef = useRef(null);

  // Preload ALL segments on mount or when segments change
  useEffect(() => {
    if (!segments.length) return;
    
    // Clear old preloaded videos
    preloadedVideosRef.current.forEach(video => {
      if (video) {
        video.pause();
        video.src = '';
      }
    });
    preloadedVideosRef.current = [];
    setAllPreloaded(false);

    let loadedCount = 0;
    const totalToLoad = segments.filter(s => s.video_url).length;

    segments.forEach((segment, index) => {
      if (!segment.video_url) {
        preloadedVideosRef.current[index] = null;
        return;
      }

      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true; // Muted for preloading
      video.src = getAssetUrl(segment.video_url);
      video.currentTime = (segment.trim_start_ms || 0) / 1000;
      
      video.oncanplaythrough = () => {
        loadedCount++;
        console.log(`Preloaded segment ${index + 1}/${segments.length}`);
        if (loadedCount >= totalToLoad) {
          setAllPreloaded(true);
          console.log('All segments preloaded!');
        }
      };
      
      video.onerror = () => {
        console.warn(`Failed to preload segment ${index}`);
        loadedCount++;
        if (loadedCount >= totalToLoad) {
          setAllPreloaded(true);
        }
      };

      preloadedVideosRef.current[index] = video;
    });

    // Cleanup
    return () => {
      preloadedVideosRef.current.forEach(video => {
        if (video) {
          video.pause();
          video.src = '';
        }
      });
    };
  }, [segments, getAssetUrl]);

  // Load a specific segment into the main video element
  const loadSegmentAtIndex = useCallback((index) => {
    const video = videoRef?.current;
    if (!video || !segments.length || index < 0 || index >= segments.length) {
      return;
    }

    const segment = segments[index];
    if (!segment.video_url) {
      console.log('No video URL for segment', index);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Use preloaded video if available
    const preloaded = preloadedVideosRef.current[index];
    if (preloaded && preloaded.readyState >= 3) {
      video.src = preloaded.src;
      video.currentTime = (segment.trim_start_ms || 0) / 1000;
      setIsLoading(false);
    } else {
      video.src = getAssetUrl(segment.video_url);
      video.currentTime = (segment.trim_start_ms || 0) / 1000;
    }

    // Apply segment speed
    video.playbackRate = Math.max(0.25, Math.min(10, segment.speed ?? 1));
    video.volume = 1;
    video.muted = false;

    setCurrentSegmentIndex(index);
    lastPositionRef.current = segment.start_ms || 0;
    onPositionChange?.(segment.start_ms || 0);
    onSegmentChange?.(segment, index);

  }, [videoRef, segments, getAssetUrl, onPositionChange, onSegmentChange]);

  // RAF loop - Master clock approach
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const updatePlayhead = () => {
      if (!isPlaying) {
        rafIdRef.current = null;
        playStartTimeRef.current = null;
        playStartPositionRef.current = null;
        return;
      }

      // Initialize master clock
      if (playStartTimeRef.current === null) {
        playStartTimeRef.current = performance.now();
        playStartPositionRef.current = lastPositionRef.current;
      }

      // Calculate timeline position from wall-clock
      const wallClockElapsed = performance.now() - playStartTimeRef.current;
      const timelinePosition = playStartPositionRef.current + wallClockElapsed;

      // Update playhead
      if (Math.abs(timelinePosition - lastPositionRef.current) > 0.1) {
        lastPositionRef.current = timelinePosition;
        onPositionChange?.(timelinePosition);
      }

      // Check if need to load next segment
      const segmentIndex = currentSegmentIndex;
      if (segmentIndex >= 0 && segmentIndex < segments.length) {
        const segment = segments[segmentIndex];
        const segmentEnd = (segment.start_ms || 0) + getEffectiveDuration(segment);

        if (timelinePosition >= segmentEnd) {
          if (segmentIndex < segments.length - 1) {
            // Load next segment
            loadSegmentAtIndex(segmentIndex + 1);
            // Reset master clock for new segment
            playStartTimeRef.current = performance.now();
            playStartPositionRef.current = segments[segmentIndex + 1].start_ms || 0;
          } else {
            // End of timeline
            onSeekComplete?.(timelinePosition);
            return;
          }
        }
      }

      rafIdRef.current = requestAnimationFrame(updatePlayhead);
    };

    if (isPlaying) {
      if (!rafIdRef.current) {
        playStartTimeRef.current = null;
        playStartPositionRef.current = null;
        rafIdRef.current = requestAnimationFrame(updatePlayhead);
      }
    } else {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [videoRef, isPlaying, currentSegmentIndex, segments, onPositionChange, onSeekComplete, loadSegmentAtIndex]);

  // Handle video events
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleCanPlay = () => {
      setIsLoading(false);
      if (isPlaying) {
        video.play().catch(e => console.log('Play error:', e.message));
      }
    };

    const handleEnded = () => {
      console.log('Segment video ended');
      // RAF loop handles transition
    };

    const handleError = () => {
      console.log('Video error:', video.error?.message);
      setError('load-error');
      setIsLoading(false);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [videoRef, isPlaying]);

  // Play/pause based on isPlaying
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    if (isPlaying && !isLoading && !error && video.src) {
      video.play().catch(e => console.log('Play error:', e.message));
    } else if (!isPlaying) {
      video.pause();
    }
  }, [videoRef, isPlaying, isLoading, error]);

  // Load first segment when ready
  useEffect(() => {
    if (segments.length > 0 && currentSegmentIndex === -1) {
      loadSegmentAtIndex(0);
    }
  }, [segments.length, currentSegmentIndex, loadSegmentAtIndex]);

  // Seek to position
  const seekToPosition = useCallback((positionMs) => {
    const video = videoRef?.current;
    if (!video || !segments.length) return;

    // Find which segment this position falls within
    let targetIndex = 0;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const segmentEnd = (segment.start_ms || 0) + getEffectiveDuration(segment);
      if (positionMs < segmentEnd) {
        targetIndex = i;
        break;
      }
      targetIndex = i;
    }

    const segment = segments[targetIndex];
    const segmentStart = segment.start_ms || 0;
    const offsetInSegment = Math.max(0, positionMs - segmentStart);
    const speed = segment.speed ?? 1;
    const videoTime = (segment.trim_start_ms || 0) + (offsetInSegment * speed);

    lastPositionRef.current = positionMs;
    onPositionChange?.(positionMs);

    if (targetIndex !== currentSegmentIndex) {
      loadSegmentAtIndex(targetIndex);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = videoTime / 1000;
        }
      }, 50);
    } else {
      video.currentTime = Math.max(0, videoTime / 1000);
    }
  }, [videoRef, segments, currentSegmentIndex, loadSegmentAtIndex, onPositionChange]);

  return {
    currentSegment: segments[currentSegmentIndex] || null,
    currentSegmentIndex,
    isLoading,
    error,
    allPreloaded,
    seekToPosition,
    loadSegment: loadSegmentAtIndex
  };
}

export default useSegmentVideoPlayer;
